import {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import {
  resolveVideoUrls,
  type TitleMedia,
} from "@story/data-centers-hub/config/titleAssets";
import { useHubMotionOn } from "@story/data-centers-hub/hooks/useHubMotionEnabled";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import getBrowserInfo from "src/utils/get-browser-info";

import * as styles from "./TitleSlide.css";

/** Keep `src` through chapter wipes, then drop so iOS can reclaim decoders. */
const RELEASE_SRC_MS = 1600;

interface Props {
  media: TitleMedia | null | undefined;
  /**
   * While true, attach the video `src`. Stays attached briefly after false so
   * wipes don't poster-snap; then detaches (poster only) to limit iOS memory.
   */
  active: boolean;
  /**
   * When false with an attached video, keep `src` but don't play. Title uses
   * this under the intro note; chapters pass on-stage.
   */
  playback?: boolean;
}

function configureInlineVideo(video: HTMLVideoElement): void {
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.loop = true;
}

/** False during SSR/hydration so we do not emit video `src` in static HTML. */
function useIsClient(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}

/**
 * Full-bleed still or muted loop for a title-grid cell / split pane.
 * Matches data-centers AmbientLedeVideo: client-only `src`, Safari/iOS CSS
 * `background-image: url(mp4)` fill. Motion-off → poster.
 *
 * On-stage: attach + play/pause. Off-stage: keep `src` through the wipe, then
 * detach — sticky-forever loops were crashing iOS Chrome after repeated hops.
 * Poster until a decoded frame exists — `playing` alone still flashed black
 * when the wipe revealed a cell at readyState 0–1 (common on Android hops).
 */
const TitleCellMedia: FunctionComponent<Props> = ({
  media,
  active,
  playback = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isClient = useIsClient();
  const motionOn = useHubMotionOn();
  const narrow = useHubNarrowViewport();
  const cssVideoFill = getBrowserInfo()?.needsCssVideoBackground ?? false;
  /** True only while the element is actually painting frames. */
  const [isPlaying, setIsPlaying] = useState(false);
  /** Decoded frame available — tighter than `playing` for wipe reveals. */
  const [hasFrame, setHasFrame] = useState(false);
  const [kept, setKept] = useState(false);

  const resolved =
    media?.kind === "video" ? resolveVideoUrls(media, narrow) : null;

  const canVideo = isClient && motionOn && Boolean(resolved?.src);

  useEffect(() => {
    if (!canVideo) {
      setKept(false);
      return;
    }
    if (active) {
      setKept(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setKept(false);
    }, RELEASE_SRC_MS);
    return () => window.clearTimeout(timer);
  }, [canVideo, active]);

  // Detach via omitting `src` — keep `key` on the resolved URL so toggling
  // `kept`/motion does not remount the node (that replayed chapter wipes).
  const videoSrc = canVideo && kept && resolved ? resolved.src : null;
  const posterSrc =
    resolved?.poster ?? (media?.kind === "video" ? media.poster : "");
  const shouldPlay = Boolean(videoSrc && playback);
  // Poster until a decoded frame exists (Blink black-box). On WebKit, also
  // keep the poster under the CSS mp4 fill — that fill can drop mid-scroll
  // and leave an empty cell if nothing is underneath.
  const showPosterCover =
    !shouldPlay || !hasFrame || (cssVideoFill && shouldPlay);
  const showSafariFill = cssVideoFill && shouldPlay;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) {
      setIsPlaying(false);
      setHasFrame(false);
      return;
    }
    configureInlineVideo(video);

    const noteFrame = () => {
      if (video.readyState >= 2) {
        setHasFrame(true);
      }
    };

    // Warm neighborhood: keep `src`, preload a frame, stay paused. Clearing
    // hasFrame here is what made hop 2+ flash after the first clean pass.
    if (!shouldPlay) {
      video.pause();
      setIsPlaying(false);
      noteFrame();
      const onLoadedData = () => {
        noteFrame();
      };
      video.addEventListener("loadeddata", onLoadedData);
      video.addEventListener("canplay", onLoadedData);
      if (video.readyState < 2) {
        try {
          video.load();
        } catch {
          // ignore
        }
      }
      return () => {
        video.removeEventListener("loadeddata", onLoadedData);
        video.removeEventListener("canplay", onLoadedData);
      };
    }

    // Cover with poster until we have current data again — but keep a frame
    // we already decoded across play↔pause toggles.
    setIsPlaying(false);
    noteFrame();

    const markFrame = () => {
      noteFrame();
      setIsPlaying(true);
    };

    const tryPlay = () => {
      void video.play().catch(() => {
        setIsPlaying(false);
      });
    };

    tryPlay();

    const onPlaying = () => {
      markFrame();
    };
    const onLoadedData = () => {
      noteFrame();
      tryPlay();
    };
    const onPause = () => {
      // Re-kick after scroll/decode stalls — but do NOT clear hasFrame /
      // isPlaying. That poster-snapped mid-wipe whenever park/scroll paused
      // the element for a tick (intermittent Android flash).
      if (!video.ended) {
        tryPlay();
      }
    };
    const onEnded = () => {
      video.currentTime = 0;
      tryPlay();
    };

    video.addEventListener("playing", onPlaying);
    video.addEventListener("loadeddata", onLoadedData);
    video.addEventListener("canplay", onLoadedData);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("canplay", onLoadedData);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.pause();
    };
  }, [videoSrc, shouldPlay]);

  if (!media) {
    return null;
  }

  // Eager once mounted — wipe chapters only mount when the prior snap pane
  // is nearby, so lazy here was the pop-in during the reveal.
  // Cells are decorative. The slide's hed and dek carry the accessible name.
  if (media.kind === "image") {
    return (
      <img
        className={styles.media}
        src={media.src}
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="eager"
        fetchPriority={active ? "high" : "low"}
      />
    );
  }

  if (!videoSrc) {
    return (
      <img
        className={styles.media}
        src={posterSrc}
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="eager"
        fetchPriority={active ? "high" : "low"}
      />
    );
  }

  return (
    <div className={styles.mediaStage}>
      <video
        key={resolved?.src ?? posterSrc}
        ref={videoRef}
        className={styles.media}
        src={videoSrc ?? undefined}
        poster={posterSrc}
        autoPlay={shouldPlay}
        loop
        muted
        playsInline
        controls={false}
        preload="auto"
        aria-hidden="true"
      />
      {showPosterCover ? (
        <img
          className={styles.mediaPosterCover}
          src={posterSrc}
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="eager"
        />
      ) : null}
      {showSafariFill ? (
        <div
          className={styles.safariVideoFill}
          style={{ backgroundImage: `url("${videoSrc}")` }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
};

export default TitleCellMedia;
